import React, { useState } from 'react';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const HugEmoji = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const hugEmojis = {
    huggingFace: '🤗',
    peopleHugging: '🫂',
    coupleWithHeart: '💑',
    couplesKiss: '💏',
    twoHearts: '💕',
    sparklingHeart: '💖',
    beatingHeart: '💗',
    growingHeart: '💗',
    heartWithRibbon: '💝',
    revolvingHearts: '💞'
  };

  const handleCopy = (emoji: string) => {
    navigator.clipboard.writeText(emoji);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What does the hugging face emoji mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The hugging face emoji 🤗 represents warmth, affection, and friendliness. It's used to express hugs, comfort, support, and general happiness or gratitude."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between 🤗 and 🫂?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The hugging face 🤗 shows a smiling face with open hands, representing a virtual hug or warm feelings. The people hugging emoji 🫂 shows two people embracing, representing actual physical hugs or close relationships."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Hug Emoji 🤗 Complete Guide & Meanings [{currentYear}] 🫂</title>
        <meta name="description" content={`【COMPLETE GUIDE】 Learn all about hug emojis 🤗 Meanings, usage & examples 🫂 Updated for ${currentYear} ✨ Copy & paste all hugging emojis 💝 Perfect for expressing warmth & affection!`} />
        <link rel="canonical" href="https://emojicollection.net/emoji/hug" />
        
        <meta property="og:title" content={`Hug Emoji 🤗 Complete Guide & Meanings [${currentYear}] 🫂`} />
        <meta property="og:description" content={`Learn all about hug emojis 🤗 Meanings, usage & examples 🫂 Updated for ${currentYear} ✨ Copy & paste all hugging emojis!`} />
        <meta property="og:url" content="https://emojicollection.net/emoji/hug" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Hug Emoji 🤗 Complete Guide & Meanings [${currentYear}] 🫂`} />
        <meta name="twitter:description" content={`Learn all about hug emojis 🤗 Meanings, usage & examples 🫂 Updated for ${currentYear} ✨ Copy & paste all hugging emojis!`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="hug emoji, hugging face emoji, hugging emoji meaning, hug emoji copy paste, people hugging emoji, virtual hug emoji, emoji hugs" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=1200&q=80"
                alt="Expressing warmth and affection with emojis"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Hug Emoji 🤗 Complete Guide & Meanings [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Discover everything about hug emojis - from their meanings and usage to platform differences. Updated for {currentYear} with the latest designs and examples.
              </p>
            </header>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">All Hug Emojis</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(hugEmojis).map(([key, emoji]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-center mb-2">
                      <span className="text-4xl">{emoji}</span>
                    </div>
                    <button
                      onClick={() => handleCopy(emoji)}
                      className={`w-full inline-flex items-center justify-center px-3 py-2 rounded text-sm font-medium ${
                        copied ? 'bg-green-500 text-white' : 'bg-blue-500 text-white hover:bg-blue-600'
                      } transition-colors`}
                    >
                      {copied ? (
                        <>
                          <Check className="mr-1" size={14} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="mr-1" size={14} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="prose prose-lg max-w-none">
              <h2>Meaning & Usage</h2>
              <p>
                Hug emojis come in several variations, each expressing different types of affection and warmth:
              </p>
              <ul>
                <li><strong>Hugging Face 🤗</strong> - Warmth, friendliness, virtual hugs</li>
                <li><strong>People Hugging 🫂</strong> - Physical embrace, support, comfort</li>
                <li><strong>Couple with Heart 💑</strong> - Romantic love, relationships</li>
                <li><strong>Sparkling Heart 💖</strong> - Affection, love, appreciation</li>
                <li><strong>Two Hearts 💕</strong> - Love, friendship, close bonds</li>
              </ul>

              <h2>Platform Differences</h2>
              <p>
                Hug emojis may appear slightly different across platforms:
              </p>
              <ul>
                <li><strong>Apple:</strong> Warm, friendly expressions with detailed features</li>
                <li><strong>Google:</strong> Simple, clear designs with bold outlines</li>
                <li><strong>Samsung:</strong> Expressive faces with dimensional effects</li>
                <li><strong>Microsoft:</strong> Clean, professional appearance</li>
              </ul>

              <h2>Common Uses</h2>
              <p>
                Hug emojis serve various purposes in digital communication:
              </p>
              <ul>
                <li>Expressing affection and care</li>
                <li>Offering comfort and support</li>
                <li>Showing appreciation and gratitude</li>
                <li>Celebrating relationships and friendships</li>
                <li>Sending virtual hugs when apart</li>
              </ul>

              <h2>Cultural Impact</h2>
              <p>
                The hug emoji has become essential in digital communication:
              </p>
              <ul>
                <li>Universal symbol of warmth and support</li>
                <li>Important for remote relationships</li>
                <li>Used across cultures and age groups</li>
                <li>Key element in emotional expression</li>
              </ul>

              <h2>Best Practices</h2>
              <p>
                To use hug emojis effectively:
              </p>
              <ul>
                <li>Choose the appropriate type for your relationship</li>
                <li>Consider cultural context and recipient</li>
                <li>Combine with supportive messages</li>
                <li>Use in both casual and caring contexts</li>
              </ul>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What does the hugging face emoji mean?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    The hugging face emoji 🤗 represents warmth, affection, and friendliness. It's used to express hugs, comfort, support, and general happiness or gratitude.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What's the difference between 🤗 and 🫂?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    The hugging face 🤗 shows a smiling face with open hands, representing a virtual hug or warm feelings. The people hugging emoji 🫂 shows two people embracing, representing actual physical hugs or close relationships.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">When should I use hug emojis?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Hug emojis are perfect for showing support, expressing affection, offering comfort, celebrating good news, or sending virtual hugs to friends and loved ones when you can't be there in person.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default HugEmoji;